import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import { useProfile } from "@/hooks/useProfile";
import { sortSpeciesByDisplayOrder } from "@/lib/species";

// Hook genérico para tabelas de lookup
type PublicTableName = "genders" | "document_types" | "lead_sources" | "states" | "pet_genders" | "pet_sterilizations" | "species" | "coat_types" | "pet_statuses";

function useLookupTable<T extends LookupItem>(tableName: PublicTableName) {
  return useQuery({
    queryKey: [tableName],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(tableName)
        .select()
        .order("name");

      if (error) throw error;
      const rows = data as T[];
      return tableName === "species"
        ? (sortSpeciesByDisplayOrder(rows as Array<{ name?: string | null }>) as T[])
        : rows;
    },
    staleTime: 1000 * 60 * 60, // 1 hora - lookup tables raramente mudam
  });
}

// Tipos para lookup tables
export interface LookupItem {
  id: string;
  name: string;
  created_at?: string;
}

export interface DocumentType extends LookupItem {
  mask: string | null;
}

export interface Breed extends LookupItem {
  species_id: string;
  customer_id?: string | null;
}

export interface City extends LookupItem {
  state_id: string;
  ibge_code: string | null;
}

export interface State extends LookupItem {
  code: string;
}

export interface Condition extends LookupItem {
  type: "condition" | "allergy" | "temperament";
  clinic_id?: string | null;
}

// Hooks específicos para cada lookup table
export const useGenders = () => useLookupTable<LookupItem>("genders");
export const useDocumentTypes = () => useLookupTable<DocumentType>("document_types");
export const useLeadSources = () => useLookupTable<LookupItem>("lead_sources");
export const useStates = () => useLookupTable<State>("states");

export const useCities = (stateId?: string) => {
  return useQuery({
    queryKey: ["cities", stateId],
    queryFn: async () => {
      let query = supabase.from("cities").select("*").order("name");

      if (stateId) {
        query = query.eq("state_id", stateId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as City[];
    },
    enabled: !stateId || !!stateId,
    staleTime: 1000 * 60 * 60,
  });
};

export const usePetGenders = () => useLookupTable<LookupItem>("pet_genders");
export const usePetSterilizations = () => useLookupTable<LookupItem>("pet_sterilizations");
export const useSpecies = () => useLookupTable<LookupItem>("species");

export const useBreeds = (speciesId?: string) => {
  const { data: customerId } = useCurrentClinicCustomerId();

  return useQuery({
    queryKey: ["breeds", customerId ?? null, speciesId ?? null],
    queryFn: async () => {
      let query = supabase.from("breeds").select("*").order("name");

      if (speciesId) {
        query = query.eq("species_id", speciesId);
      }

      if (customerId) {
        query = query.or(`customer_id.is.null,customer_id.eq.${customerId}`);
      } else {
        query = query.or("customer_id.is.null");
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Breed[];
    },
    enabled: !speciesId || !!speciesId,
    staleTime: 1000 * 60 * 60,
  });
};

const ADDITIONAL_COLOR_NAMES = [
  "Merle",
  "Preto e Branco",
  "Branco e beje",
  "Bicolor",
  "Chocolate",
];

export const useCoatTypes = () => useLookupTable<LookupItem>("coat_types");
export const useColors = () => {
  return useQuery({
    queryKey: ["colors"],
    queryFn: async () => {
      const { data, error } = await supabase.from("colors").select("*").order("name");
      if (error) throw error;
      const colors = (data ?? []) as LookupItem[];

      const existingNames = colors.map((color) => color.name?.toLowerCase().trim());
      const missing = ADDITIONAL_COLOR_NAMES.filter(
        (name) => !existingNames.includes(name.toLowerCase()),
      );

      if (missing.length > 0) {
        const { data: inserted, error: insertError } = await supabase
          .from("colors")
          .insert(missing.map((name) => ({ name })))
          .select("*");

        if (!insertError && inserted) {
          const merged = [...colors, ...(inserted as LookupItem[])];
          return merged.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
        }
      }

      return colors;
    },
    staleTime: 1000 * 60 * 60,
  });
};
export const usePetStatuses = () => {
  const query = useLookupTable<LookupItem>("pet_statuses");
  const filteredData = useMemo(() => {
    if (!query.data) return query.data;
    return query.data.filter(
      (status) => status.name?.toLowerCase().trim() !== "transferido",
    );
  }, [query.data]);

  return {
    ...query,
    data: filteredData,
  } as typeof query;
};

export const useConditions = (type?: "condition" | "allergy" | "temperament") => {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;
  const needsClinic = type === "allergy" || type === "temperament";

  return useQuery({
    queryKey: ["conditions", type ?? "all", needsClinic ? clinicId : "global"],
    queryFn: async () => {
      let query = supabase.from("conditions").select("*").order("name");

      if (type) {
        query = query.eq("type", type);
      }

      if (needsClinic) {
        if (!clinicId) return [];
        query = query.eq("clinic_id", clinicId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Condition[];
    },
    enabled: profile !== undefined && (!needsClinic || !!clinicId),
    staleTime: 1000 * 60 * 60,
  });
};

/** Allergies for the current clinic (Configurações > Geral). */
export const useClinicAllergies = () => useConditions("allergy");

/** Temperaments for the current clinic (Configurações > Geral). */
export const useClinicTemperaments = () => useConditions("temperament");

export const useTemperaments = () => useConditions("temperament");
